import React, { Fragment, PureComponent, useCallback, useEffect, useMemo, useState } from "react";
import { getRequest } from "../../app/request";
import { usePopupContext } from "../Popup/PopupContext";
import { Button, Col, FormGroup, FormSelect, Row, Table } from "react-bootstrap";
import amountFormat from "../../utils/amountFormat";
import { useLocation } from "react-router";
import DatePicker from "../../components/DatePicker/DatePicker";
import moment from "moment-timezone";
import appHistory from "../../app/appHistory";
import _ from "lodash";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Link } from "react-router-dom";
import { BinIcon } from "../../components/Icon/Icon";

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="custom-tooltip">
                <h4>${amountFormat(payload[0].value, 2)}</h4>
                <p className="label">{moment(label).toString()}</p>
            </div>
        );
    }

    return null;
};

class CustomizedYAxisTick extends PureComponent {
    render() {
        const { x, y, stroke, payload } = this.props;

        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={1} textAnchor="end" fill="#666">
                    {amountFormat(payload.value, 2, null, false, {
                        toFixed: true,
                    })}
                </text>
            </g>
        );
    }
}

export default function BalanceLogs() {
    const [list, setList] = useState([]);
    const [chartList, setChartList] = useState([]);
    const { catchPopup } = usePopupContext();
    const { search } = useLocation();

    const [from, setFrom] = useState(moment().utc().startOf('week').toDate());
    const [to, setTo] = useState(moment().utc().endOf('week').toDate());
    const [showChart, setShowChart] = useState(true);

    const searchParams = useMemo(() => {
        const urlParams = new URLSearchParams(search);
        return {
            from,
            to,
            limit: +(urlParams.get('limit') || 50),
            offset: +(urlParams.get('offset') || 0),
            search: urlParams.get('search'),
            type: urlParams.get('type'),
        };
    }, [search, from, to]);

    const getBalanceLogs = useCallback(() => {
        const urlParams = new URLSearchParams(search);
        getRequest(`/users/balance-logs?${urlParams.toString()}`).then(res => {
            setList(res);
        }).catch(e => {
            catchPopup(e)
        });
    }, [catchPopup, search]);

    const getBalanceDayLogs = useCallback(() => {
        const urlParams = new URLSearchParams(search);
        getRequest(`/users/balance-day-logs?${urlParams.toString()}`).then(res => {
            setChartList(res);
        }).catch(e => {
            catchPopup(e)
        });
    }, [catchPopup, search]);

    const onChangeFromHandler = useCallback((date) => {
        const urlParams = new URLSearchParams(search);
        urlParams.set('from', moment(date).utc().startOf('day').toISOString());
        appHistory.push({
            search: urlParams.toString(),
        });
    }, [search]);

    const onChangeToHandler = useCallback((date) => {
        const urlParams = new URLSearchParams(search);
        urlParams.set('to', moment(date).utc().endOf('day').toISOString());
        appHistory.push({
            search: urlParams.toString(),
        });
    }, [search]);

    const changeParam = useCallback((name, value) => {
        const urlParams = new URLSearchParams(search);
        urlParams.set(name, _.max([value, 0]).toString());
        appHistory.push({
            search: urlParams.toString(),
        });
    }, [search]);

    const dataChart = useMemo(() => {
        return _.sortBy(chartList, 'created_at').map(item => ({
            date: moment(item.created_at).toDate(),
            balance: _.round(item.balance, 2),
        }));
    }, [chartList]);

    const onToggleShowChart = useCallback(() => {
        setShowChart(state => !state);
    }, []);

    const filterChangeHandler = useCallback((e) => {
        const field = e.target.name;
        const value = e.target.value;
        changeParam(field, value);
    }, [changeParam]);

    const clearFilterHandler = useCallback((e) => {
        e.preventDefault();
        const name = e.currentTarget.dataset.filter;
        const urlParams = new URLSearchParams(search);
        urlParams.delete(name);
        appHistory.push({
            search: urlParams.toString(),
        });
    }, [search]);

    const domain = useMemo(() => {
        if (dataChart.length) {
            return [
                moment(_.first(dataChart).date).unix() * 1000,
                moment(_.last(dataChart).date).unix() * 1000
            ];
        }
    }, [dataChart]);

    const tickFormatterHandler = useCallback((date) => {
        const from = _.first(dataChart).date;
        const to = _.last(dataChart).date;

        if (moment(from).diff(moment(to), 'd') > 20) {
            return moment(date).format('DD-MM-YY');
        } else if (moment(from).diff(moment(to), 'd') > 1) {
            return moment(date).format('DD-MM');
        } else {
            return moment(date).format('HH:mm:ss');
        }
    }, [dataChart]);

    useEffect(() => {
        getBalanceLogs();
        getBalanceDayLogs();
    }, [getBalanceDayLogs, getBalanceLogs, searchParams]);

    useEffect(() => {
        const urlParams = new URLSearchParams(search);
        const from = urlParams.get('from');
        const to = urlParams.get('to');
        if (from) setFrom(moment(from).toDate());
        if (to) setTo(moment(to).toDate());
    }, [search]);

    return <div>

        {
            showChart && dataChart.length > 0 && <>

                <h4>Daily Log for {moment(dataChart[0].date).format('DD-MM-YYYY')}</h4>

                <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={dataChart}
                               margin={{ top: 5, right: 20, left: 30, bottom: 5 }}>
                        <XAxis dataKey="date"
                               tickFormatter={tickFormatterHandler}

                               type="number"
                               domain={domain}
                        />
                        <YAxis allowDataOverflow domain={['dataMin', 'dataMax']} type="number" yAxisId="0"
                               tick={<CustomizedYAxisTick/>}/>
                        <Tooltip content={<CustomTooltip/>}/>
                        {/*<CartesianGrid stroke="#f5f5f5"/>*/}
                        <Line type="monotone" dataKey="balance" stroke="#ff7300" yAxisId="0" dot={false}/>
                    </LineChart>
                </ResponsiveContainer>
            </>
        }

        <h3>Balance Log</h3>

        <FormGroup className={'p-3'} style={{ background: 'var(--color-bg-modal)' }}>
            <Row>
                <Col>From</Col>
                <Col>To</Col>
                <Col>Type</Col>
                <Col>Records {searchParams.limit} from {searchParams.offset}</Col>
            </Row>
            <Row>
                <Col><DatePicker selected={from} onChange={onChangeFromHandler}/></Col>
                <Col><DatePicker selected={to} onChange={onChangeToHandler}/></Col>
                <Col>
                    <FormSelect name={'type'} onChange={filterChangeHandler}>
                        <option value={''}>-</option>
                        {
                            ['commission', 'settlement'].map(type => <Fragment key={type}>
                                <option value={type} selected={searchParams.type === type}>{type}</option>
                            </Fragment>)
                        }
                    </FormSelect>
                </Col>
                <Col>
                    <Button variant={'secondary'} name={'offset'}
                            onClick={() => changeParam('offset', searchParams.offset + searchParams.limit)}>«</Button>
                    <Button variant={'secondary'} name={'offset'}
                            disabled={searchParams.offset <= 0}
                            onClick={() => changeParam('offset', searchParams.offset - searchParams.limit)}>»</Button>
                </Col>
            </Row>
        </FormGroup>

        <Table striped bordered hover variant="dark">
            <thead>
            <tr>
                <th>Datetime</th>
                <th>Type {
                    searchParams.type && <Link to={'#'} data-filter={'type'}
                                               onClick={clearFilterHandler}><BinIcon
                        style={{ color: 'var(--color-negative)' }}
                        name={'IcCloseWhite'}/></Link>
                }</th>
                <th>Reason</th>
                <th>Amount</th>
                <th>Balance</th>
            </tr>
            </thead>
            <tbody>
            {
                list.map(item => <Fragment key={item.id}>
                    <tr>
                        <td>{item.created_at}</td>
                        <td>{item.type}</td>
                        <td>{item.reason}</td>
                        <td style={{
                            color: item.amount > 0 ? 'var(--color-positive)' : item.amount < 0 ? 'var(--color-negative)' : '',
                        }}>{amountFormat(item.amount, 6, '$')}</td>
                        <td>{amountFormat(item.balance, 6, '$')}</td>
                    </tr>
                </Fragment>)
            }
            </tbody>
        </Table>

    </div>
}
